import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  IconButton,
  Box,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import IconOnlyChip from './IconOnlyChip';
import { fetchDepartments, fetchCourses } from '../services/api';
import { formatPrevSubmission } from '../utils/formatPrevSubmission';

const EditCourseDialog = ({ open, onClose, course: courseProp, onResubmit }) => {
  const course = courseProp ?? {};
  const [editShort, setEditShort] = useState(false);
  const [editLong, setEditLong] = useState(false);
  const [editDept, setEditDept] = useState(false);
  const [shortName, setShortName] = useState('');
  const [longName, setLongName] = useState('');
  const [deptId, setDeptId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [shortNameError, setShortNameError] = useState('');
  const [longNameError, setLongNameError] = useState('');

  useEffect(() => {
    if (open) {
      setEditShort(false);
      setEditLong(false);
      setEditDept(false);
      setShortName('');
      setLongName('');
      setShortNameError('');
      setLongNameError('');
      const instId = courseProp?.department?.institution?.id;
      setDeptId(courseProp?.department?.id || '');
      if (instId) {
        fetchDepartments(instId)
          .then((res) => setDepartments(res || []))
          .catch(() => setDepartments([]));
        fetchCourses(instId)
          .then((res) => {
            const list = res.results || res;
            setCourses(list || []);
          })
          .catch(() => setCourses([]));
      } else {
        setDepartments([]);
        setCourses([]);
      }
    }
  }, [open, courseProp]);

  useEffect(() => {
    if (!shortName) {
      setShortNameError('');
    } else {
      const dup = courses.some(
        (c) =>
          c.id !== course.id &&
          c.short_name?.toLowerCase().trim() ===
            shortName.trim().toLowerCase()
      );
      setShortNameError(dup ? 'Short name already exists' : '');
    }
  }, [shortName, courses, course.id]);

  useEffect(() => {
    if (!longName) {
      setLongNameError('');
    } else {
      const dup = courses.some(
        (c) =>
          c.id !== course.id &&
          c.long_name?.toLowerCase().trim() ===
            longName.trim().toLowerCase()
      );
      setLongNameError(dup ? 'Long name already exists' : '');
    }
  }, [longName, courses, course.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!courseProp) return;
    const payload = {};
    if (editDept) payload.department = deptId || course.department?.id;
    if (editShort) payload.short_name = shortName || course.short_name;
    if (editLong) payload.long_name = longName || course.long_name;
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    if (onResubmit) onResubmit(course.id, payload);
  };

  const renderField = (
    label,
    current,
    editing,
    setEditing,
    value,
    setValue,
    error = '',
    setError
  ) => (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography>{current || '—'}</Typography>
        {editing ? (
          <>
            <TextField
              size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="New Value"
              error={!!error}
              helperText={error}
            />
            <IconOnlyChip
              icon={<CancelIcon fontSize="small" />}
              onClick={() => {
                setEditing(false);
                setValue('');
                if (setError) setError('');
              }}
            />
          </>
        ) : (
          <IconOnlyChip
            icon={<EditIcon fontSize="small" />}
            onClick={() => setEditing(true)}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Edit Rejected Course</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Department
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{course?.department?.name || '—'}</Typography>
            {editDept ? (
              <>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconOnlyChip
                  icon={<CancelIcon fontSize="small" />}
                  onClick={() => {
                    setEditDept(false);
                    setDeptId('');
                  }}
                />
              </>
            ) : (
              <IconOnlyChip
                icon={<EditIcon fontSize="small" />}
                onClick={() => setEditDept(true)}
              />
            )}
          </Box>
        </Box>
        {renderField(
          'Short Name',
          course.short_name,
          editShort,
          setEditShort,
          shortName,
          setShortName,
          shortNameError,
          setShortNameError
        )}
        {renderField(
          'Long Name',
          course.long_name,
          editLong,
          setEditLong,
          longName,
          setLongName,
          longNameError,
          setLongNameError
        )}
        {course.rejection_reason && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Reason for Rejection
            </Typography>
            <Typography>{course.rejection_reason}</Typography>
          </Box>
        )}
        {course.previous_submission && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Previous Submission
            </Typography>
            <Typography>{formatPrevSubmission(course.previous_submission)}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          text="Resubmit"
          color="info"
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditCourseDialog;
